import imageCompression from 'browser-image-compression';

/**
 * Configuration options for image compression
 */
export interface CompressionOptions {
  /** Maximum width in pixels (default: 1920) */
  maxWidthOrHeight: number;
  /** Maximum file size in KB (default: 800) */
  maxSizeMB: number;
  /** JPEG quality from 0 to 1 (default: 0.85) */
  quality: number;
  /** Output format (default: 'jpeg') */
  fileType: string;
  /** Whether to use web worker for compression (default: true) */
  useWebWorker: boolean;
  /** Whether to preserve EXIF data (default: true) */
  preserveExif: boolean;
}

/**
 * Result of image compression operation
 */
export interface CompressionResult {
  /** The compressed file */
  file: File;
  /** Original file size in bytes */
  originalSize: number;
  /** Compressed file size in bytes */
  compressedSize: number;
  /** Compression ratio (0-1, where 0.5 means 50% reduction) */
  compressionRatio: number;
  /** Whether compression was successful */
  success: boolean;
  /** Error message if compression failed */
  error?: string;
}

/**
 * Progress information during compression
 */
export interface CompressionProgress {
  /** Current file being processed */
  currentFile: string;
  /** Current file index (0-based) */
  currentIndex: number;
  /** Total number of files */
  totalFiles: number;
  /** Progress percentage (0-100) */
  percentage: number;
  /** Current operation status */
  status: 'preparing' | 'compressing' | 'complete' | 'error';
  /** Estimated time remaining in milliseconds */
  estimatedTimeRemaining?: number;
  /** Total bytes processed */
  bytesProcessed?: number;
  /** Total bytes to process */
  totalBytes?: number;
}

/**
 * Service for compressing images on the client-side before upload
 */
export class ImageCompressionService {
  private static readonly DEFAULT_OPTIONS: CompressionOptions = {
    maxWidthOrHeight: 1920,
    maxSizeMB: 0.8, // 800KB
    quality: 0.85,
    fileType: 'jpeg',
    useWebWorker: true,
    preserveExif: true,
  };

  /**
   * Compress a single image file
   */
  static async compressImage(
    file: File,
    options: Partial<CompressionOptions> = {}
  ): Promise<CompressionResult> {
    const compressionOptions = { ...this.DEFAULT_OPTIONS, ...options };
    const originalSize = file.size;

    try {
      const compressedFile = await imageCompression(file, compressionOptions);
      const compressedSize = compressedFile.size;
      const compressionRatio = (originalSize - compressedSize) / originalSize;

      return {
        file: compressedFile,
        originalSize,
        compressedSize,
        compressionRatio,
        success: true,
      };
    } catch (error) {
      console.error('Image compression failed:', error);
      
      // Return original file as fallback
      return {
        file,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 0,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown compression error',
      };
    }
  }

  /**
   * Compress multiple image files with progress tracking
   */
  static async compressImages(
    files: File[],
    options: Partial<CompressionOptions> = {},
    onProgress?: (progress: CompressionProgress) => void
  ): Promise<CompressionResult[]> {
    const results: CompressionResult[] = [];
    const totalFiles = files.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Report progress
      if (onProgress) {
        onProgress({
          currentFile: file.name,
          currentIndex: i,
          totalFiles,
          percentage: Math.round((i / totalFiles) * 100),
          status: 'compressing',
        });
      }

      try {
        const result = await this.compressImage(file, options);
        results.push(result);
      } catch (error) {
        console.error(`Failed to compress ${file.name}:`, error);
        results.push({
          file,
          originalSize: file.size,
          compressedSize: file.size,
          compressionRatio: 0,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown compression error',
        });
      }
    }

    // Report completion
    if (onProgress) {
      onProgress({
        currentFile: '',
        currentIndex: files.length,
        totalFiles,
        percentage: 100,
        status: 'complete',
      });
    }

    return results;
  }

  /**
   * Check if a file needs compression based on size and dimensions
   */
  static async shouldCompress(
    file: File,
    options: Partial<CompressionOptions> = {}
  ): Promise<boolean> {
    const compressionOptions = { ...this.DEFAULT_OPTIONS, ...options };
    
    // Check file size
    if (file.size > compressionOptions.maxSizeMB * 1024 * 1024) {
      return true;
    }

    // Check dimensions if it's an image
    if (file.type.startsWith('image/')) {
      try {
        const dimensions = await this.getImageDimensions(file);
        if (
          dimensions.width > compressionOptions.maxWidthOrHeight ||
          dimensions.height > compressionOptions.maxWidthOrHeight
        ) {
          return true;
        }
      } catch (error) {
        console.warn('Could not determine image dimensions:', error);
        // If we can't determine dimensions, compress based on file size only
      }
    }

    return false;
  }

  /**
   * Get image dimensions from a file
   */
  private static getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }
}